import { useQuery } from "@tanstack/react-query";
import { DatabaseBackup } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { api, type BackupRow } from "@/lib/api";

function fmtBytes(n: number): string {
  if (n > 1 << 30) return `${(n / (1 << 30)).toFixed(1)} GiB`;
  if (n > 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MiB`;
  if (n > 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${n} B`;
}

export default function Backups() {
  const { data } = useQuery({
    queryKey: ["backups"],
    queryFn: () => api.get<{ items: BackupRow[]; note: string }>("/api/backups"),
    refetchInterval: 60_000,
  });
  const items = data?.items ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Backups & recovery"
        description="Recorded backup artifacts (both databases + BW storage + secrets). These are unencrypted archives — protect the directory and the host."
      />
      {data?.note && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300">
          {data.note}
        </div>
      )}
      <Card>
        <Table>
          <THead>
            <TR><TH>Path</TH><TH>Size</TH><TH>Contents</TH><TH>Manifest sha256</TH><TH>Recorded</TH></TR>
          </THead>
          <TBody>
            {items.map((b) => (
              <TR key={b.id}>
                <TD className="max-w-[260px] truncate font-mono text-xs" title={b.path}>{b.path}</TD>
                <TD className="text-xs">{fmtBytes(b.size_bytes)}</TD>
                <TD className="text-xs text-muted-foreground">{b.contents.length} file(s)</TD>
                <TD className="font-mono text-[10px]">{b.sha256 ? b.sha256.slice(0, 16) + "…" : "—"}</TD>
                <TD className="text-xs text-muted-foreground">
                  {new Date(b.created_at_fs ?? b.seen_at).toLocaleString()}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {items.length === 0 && (
          <EmptyState icon={DatabaseBackup} title="No backups recorded"
            hint="Backups are recorded by the update runner (pre-update) and scripts/backup.sh. An empty list means none observed — not that you're covered." />
        )}
      </Card>
      <div className="text-xs text-muted-foreground">
        Recovery procedure: <code>scripts/restore.sh</code> + the runbook in <code>docs/</code>.
        Recovery point = the backup timestamp; writes after it are not captured.
      </div>
    </div>
  );
}
